import { ModelBase } from "@shoothill/core";
import { CurrentPropertyDetailDTO } from "Globals/Models/DTO/CurrentPropertyDetailDTO";
import * as MobX from "mobx";
import { IsEmail, IsNotEmpty, Equals, IsPhoneNumber } from "class-validator";

export class CurrentPropertyDetailsModel extends ModelBase<CurrentPropertyDetailsModel, CurrentPropertyDetailsAndRelated> {
    @MobX.observable public id: string | null = null;
    @MobX.observable public formId: string = "";
    @MobX.observable public applicantId: string = "";
    @MobX.observable public homeOwnerStatusId: string = "placeholder";
    @MobX.observable public propertyOwnerName: string = "";
    @MobX.observable public addressLine1: string = "";
    @MobX.observable public addressLine2: string = "";
    @MobX.observable public addressLine3: string = "";
    @MobX.observable public city: string = "";
    @MobX.observable public county: string = "";
    @MobX.observable public postcode: string = "";
    @MobX.observable public mortgageAmountOutstanding: number | null = null;
    @MobX.observable public hasNegativeEquity: boolean = false;
    @MobX.observable public negativeEquityNotes: string = "";
    @MobX.observable public hasBuyerForProperty: boolean = false;
    @MobX.observable public isSellingWithEstateAgent: boolean = false;
    @MobX.observable public estateAgentName: string = "";

    @MobX.observable public estateAgentPhone: string = "";

    @MobX.observable public estimatedCompletedDate: Date | null = null;
    @MobX.observable public isSharedOwnership: boolean = false;
    @MobX.observable public percentageOwned: number | null = null;
    @MobX.observable public housingAssociationName: string = "";

    @MobX.observable public housingAssociationPhone: string = "";

    @MobX.observable public housingAssociationAddressLine1: string = "";
    @MobX.observable public housingAssociationAddressLine2: string = "";
    @MobX.observable public housingAssociationAddressLine3: string = "";
    @MobX.observable public housingAssociationCity: string = "";
    @MobX.observable public housingAssociationCounty: string = "";
    @MobX.observable public housingAssociationPostcode: string = "";
    @MobX.observable public movingReason: string = "";
    @MobX.observable public saleCompletedDate: Date | null = null;
    @MobX.observable public equityReceived: number | null = null; // This doubles up as Estimated Equity
    @MobX.observable public arrearsDocumentUrl: string = "";
    @MobX.observable public arrearsFileName: string = "";
    @MobX.observable public transferDate: Date | null = null;
    @MobX.observable public transferAmountReceived: number | null = null;
    @MobX.observable public saleDocumentUrl: string = "";
    @MobX.observable public saleFileName: string = "";
    @MobX.observable public sameForApplicantTwo: boolean = false;
    @MobX.observable public createdDate: Date | null = null;
    @MobX.observable public isDeleted: boolean = false;

    @MobX.action
    public fromDto = (model: CurrentPropertyDetailsAndRelated): void => {
        if (model !== null && model !== undefined) {
            this.id = model.currentPropertyDetail.id;
            this.formId = model.currentPropertyDetail.formId;
            this.applicantId = model.currentPropertyDetail.applicantId;
            this.homeOwnerStatusId = model.currentPropertyDetail.homeOwnerStatusId == "" ? "placeholder" : model.currentPropertyDetail.homeOwnerStatusId;
            this.propertyOwnerName = model.currentPropertyDetail.propertyOwnerName;
            this.addressLine1 = model.currentPropertyDetail.addressLine1;
            this.addressLine2 = model.currentPropertyDetail.addressLine2;
            this.addressLine3 = model.currentPropertyDetail.addressLine3;
            this.city = model.currentPropertyDetail.city;
            this.county = model.currentPropertyDetail.county;
            this.postcode = model.currentPropertyDetail.postcode;
            this.mortgageAmountOutstanding = model.currentPropertyDetail.mortgageAmountOutstanding;
            this.hasNegativeEquity = model.currentPropertyDetail.hasNegativeEquity;
            this.negativeEquityNotes = model.currentPropertyDetail.negativeEquityNotes;
            this.hasBuyerForProperty = model.currentPropertyDetail.hasBuyerForProperty;
            this.isSellingWithEstateAgent = model.currentPropertyDetail.isSellingWithEstateAgent;
            this.estateAgentName = model.currentPropertyDetail.estateAgentName;
            this.estateAgentPhone = model.currentPropertyDetail.estateAgentPhone;
            this.estimatedCompletedDate = model.currentPropertyDetail.estimatedCompletedDate;
            this.isSharedOwnership = model.currentPropertyDetail.isSharedOwnership;
            this.percentageOwned = model.currentPropertyDetail.percentageOwned;
            this.housingAssociationName = model.currentPropertyDetail.housingAssociationName;
            this.housingAssociationPhone = model.currentPropertyDetail.housingAssociationPhone;
            this.housingAssociationAddressLine1 = model.currentPropertyDetail.housingAssociationAddressLine1;
            this.housingAssociationAddressLine2 = model.currentPropertyDetail.housingAssociationAddressLine2;
            this.housingAssociationAddressLine3 = model.currentPropertyDetail.housingAssociationAddressLine3;
            this.housingAssociationCity = model.currentPropertyDetail.housingAssociationCity;
            this.housingAssociationCounty = model.currentPropertyDetail.housingAssociationCounty;
            this.housingAssociationPostcode = model.currentPropertyDetail.housingAssociationPostcode;
            this.movingReason = model.currentPropertyDetail.movingReason;
            this.saleCompletedDate = model.currentPropertyDetail.saleCompletedDate;
            this.equityReceived = model.currentPropertyDetail.equityReceived;
            this.arrearsDocumentUrl = model.currentPropertyDetail.arrearsDocumentUrl;
            this.arrearsFileName = model.currentPropertyDetail.arrearsFileName;
            this.transferDate = model.currentPropertyDetail.transferDate;
            this.transferAmountReceived = model.currentPropertyDetail.transferAmountReceived;
            this.saleDocumentUrl = model.currentPropertyDetail.saleDocumentUrl;
            this.saleFileName = model.currentPropertyDetail.saleFileName;
            this.sameForApplicantTwo = model.currentPropertyDetail.sameForApplicantTwo;
            this.createdDate = model.currentPropertyDetail.createdDate;
            this.isDeleted = model.currentPropertyDetail.isDeleted;
        }
    };

    @MobX.action
    public fromCurrentPropertyDetailDto = (model: CurrentPropertyDetailDTO): void => {
        if (model !== null && model !== undefined) {
            this.id = model.id;
            this.formId = model.formId;
            this.applicantId = model.applicantId;
            this.homeOwnerStatusId = model.homeOwnerStatusId == "" ? "placeholder" : model.homeOwnerStatusId;
            this.propertyOwnerName = model.propertyOwnerName;
            this.addressLine1 = model.addressLine1;
            this.addressLine2 = model.addressLine2;
            this.addressLine3 = model.addressLine3;
            this.city = model.city;
            this.county = model.county;
            this.postcode = model.postcode;
            this.mortgageAmountOutstanding = model.mortgageAmountOutstanding;
            this.hasNegativeEquity = model.hasNegativeEquity;
            this.negativeEquityNotes = model.negativeEquityNotes;
            this.hasBuyerForProperty = model.hasBuyerForProperty;
            this.isSellingWithEstateAgent = model.isSellingWithEstateAgent;
            this.estateAgentName = model.estateAgentName;
            this.estateAgentPhone = model.estateAgentPhone;
            this.estimatedCompletedDate = model.estimatedCompletedDate;
            this.isSharedOwnership = model.isSharedOwnership;
            this.percentageOwned = model.percentageOwned;
            this.housingAssociationName = model.housingAssociationName;
            this.housingAssociationPhone = model.housingAssociationPhone;
            this.housingAssociationAddressLine1 = model.housingAssociationAddressLine1;
            this.housingAssociationAddressLine2 = model.housingAssociationAddressLine2;
            this.housingAssociationAddressLine3 = model.housingAssociationAddressLine3;
            this.housingAssociationCity = model.housingAssociationCity;
            this.housingAssociationCounty = model.housingAssociationCounty;
            this.housingAssociationPostcode = model.housingAssociationPostcode;
            this.movingReason = model.movingReason;
            this.saleCompletedDate = model.saleCompletedDate;
            this.equityReceived = model.equityReceived;
            this.arrearsDocumentUrl = model.arrearsDocumentUrl;
            this.arrearsFileName = model.arrearsFileName;
            this.transferDate = model.transferDate;
            this.transferAmountReceived = model.transferAmountReceived;
            this.saleDocumentUrl = model.saleDocumentUrl;
            this.saleFileName = model.saleFileName;
            this.sameForApplicantTwo = model.sameForApplicantTwo;
            this.createdDate = model.createdDate;
            this.isDeleted = model.isDeleted;
        }
    };

    @MobX.action
    public toDto = (): CurrentPropertyDetailDTO => {
        let model: CurrentPropertyDetailDTO = {
            id: this.id,
            formId: this.formId,
            applicantId: this.applicantId,
            homeOwnerStatusId: this.homeOwnerStatusId == "placeholder" ? "" : this.homeOwnerStatusId,
            propertyOwnerName: this.propertyOwnerName,
            addressLine1: this.addressLine1,
            addressLine2: this.addressLine2,
            addressLine3: this.addressLine3,
            city: this.city,
            county: this.county,
            postcode: this.postcode,
            mortgageAmountOutstanding: this.mortgageAmountOutstanding,
            hasNegativeEquity: this.hasNegativeEquity,
            negativeEquityNotes: this.negativeEquityNotes,
            hasBuyerForProperty: this.hasBuyerForProperty,
            isSellingWithEstateAgent: this.isSellingWithEstateAgent,
            estateAgentName: this.estateAgentName,
            estateAgentPhone: this.estateAgentPhone,
            estimatedCompletedDate: this.estimatedCompletedDate,
            isSharedOwnership: this.isSharedOwnership,
            percentageOwned: this.percentageOwned,
            housingAssociationName: this.housingAssociationName,
            housingAssociationPhone: this.housingAssociationPhone,
            housingAssociationAddressLine1: this.housingAssociationAddressLine1,
            housingAssociationAddressLine2: this.housingAssociationAddressLine2,
            housingAssociationAddressLine3: this.housingAssociationAddressLine3,
            housingAssociationCity: this.housingAssociationCity,
            housingAssociationCounty: this.housingAssociationCounty,
            housingAssociationPostcode: this.housingAssociationPostcode,
            movingReason: this.movingReason,
            saleCompletedDate: this.saleCompletedDate,
            equityReceived: this.equityReceived,
            arrearsDocumentUrl: this.arrearsDocumentUrl,
            arrearsFileName: this.arrearsFileName,
            transferDate: this.transferDate,
            transferAmountReceived: this.transferAmountReceived,
            saleDocumentUrl: this.saleDocumentUrl,
            saleFileName: this.saleFileName,
            sameForApplicantTwo: this.sameForApplicantTwo,
            createdDate: this.createdDate,
            isDeleted: this.isDeleted,
        };
        return model;
    };

    @MobX.action
    public clearData = (): void => {
        this.id = null;
        this.formId = "";
        this.applicantId = "";
        this.homeOwnerStatusId = "placeholder";
        this.propertyOwnerName = "";
        this.addressLine1 = "";
        this.addressLine2 = "";
        this.addressLine3 = "";
        this.city = "";
        this.county = "";
        this.postcode = "";
        this.mortgageAmountOutstanding = null;
        this.hasNegativeEquity = true;
        this.negativeEquityNotes = "";
        this.hasBuyerForProperty = true;
        this.isSellingWithEstateAgent = true;
        this.estateAgentName = "";
        this.estateAgentPhone = "";
        this.estimatedCompletedDate = null;
        this.isSharedOwnership = true;
        this.percentageOwned = null;
        this.housingAssociationName = "";
        this.housingAssociationPhone = "";
        this.housingAssociationAddressLine1 = "";
        this.housingAssociationAddressLine2 = "";
        this.housingAssociationAddressLine3 = "";
        this.housingAssociationCity = "";
        this.housingAssociationCounty = "";
        this.housingAssociationPostcode = "";
        this.movingReason = "";
        this.saleCompletedDate = null;
        this.equityReceived = null;
        this.arrearsDocumentUrl = "";
        this.arrearsFileName = "";
        this.transferDate = null;
        this.transferAmountReceived = null;
        this.saleDocumentUrl = "";
        this.saleFileName = "";
        this.sameForApplicantTwo = false;
        this.createdDate = null;
        this.isDeleted = false;
    };
}

// Extend interface to accept new form sections
export interface CurrentPropertyDetailsAndRelated {
    currentPropertyDetail: CurrentPropertyDetailDTO;
}
